import { useCallback, useEffect, useState } from 'react';
import { standardAreaApi } from '../api/StandardAreaApi';
import { SaveStandardAreaRequest, StandardAreaDto } from '../types/standardArea.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { useUserInfo } from '@/shared/hooks/useUserInfo';

export const useStandardAreaManagement = () => {
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    const [areas, setAreas] = useState<StandardAreaDto[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchAreas = useCallback(async () => {
        setLoading(true);
        try {
            const res = await standardAreaApi.getStandardAreas();
            setAreas(res.areas ?? []);
        } catch {
            showToast('표준권역 목록 조회에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        fetchAreas();
    }, [fetchAreas]);

    const saveArea = useCallback(async (req: SaveStandardAreaRequest): Promise<boolean> => {
        const userInfo = getUserInfo();
        if (!userInfo) return false;
        try {
            await standardAreaApi.saveStandardArea({ ...req, userId: userInfo.userId });
            showToast(req.seqNo ? '수정되었습니다.' : '등록되었습니다.', { type: ToastType.SUCCESS });
            await fetchAreas();
            return true;
        } catch (error: any) {
            const msg =
                error?.originalError?.response?.data?.message ||
                error?.message ||
                '저장에 실패했습니다.';
            showToast(msg, { type: ToastType.ERROR });
            return false;
        }
    }, [getUserInfo, showToast, fetchAreas]);

    const deleteArea = useCallback(async (seqNo: number): Promise<boolean> => {
        try {
            await standardAreaApi.deleteStandardArea(seqNo);
            showToast('삭제되었습니다.', { type: ToastType.SUCCESS });
            await fetchAreas();
            return true;
        } catch {
            showToast('삭제에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        }
    }, [showToast, fetchAreas]);

    const autoRegister = useCallback(async (): Promise<boolean> => {
        const userInfo = getUserInfo();
        if (!userInfo) return false;
        try {
            const result = await standardAreaApi.autoRegister(userInfo.userId);
            showToast(
                `자동 등록 완료: 권역 ${result.newAreaCount}개, 매핑 ${result.newMappingCount}개 신규 추가`,
                { type: ToastType.SUCCESS }
            );
            await fetchAreas();
            return true;
        } catch {
            showToast('자동 등록에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        }
    }, [getUserInfo, showToast, fetchAreas]);

    return { areas, loading, saveArea, deleteArea, autoRegister };
};
